import { Event } from "../../../base/common/events.js";
import { createDecorator } from "../../instantiation/common/registry.js";

export const IInspectionService = createDecorator<IInspectionService>("inspectionService");

/**
 * A request to inspect an object (show it in the Inspector, node graph, …).
 */
export interface InspectRequest {
    /**
     * The object to inspect. Consumers must NOT retain it beyond handling the
     * request (the chrome-extension build must never extend host-page object
     * lifetimes) — deref/copy what you need.
     */
    readonly target: object;
    /** Where the request came from, e.g. "resources", "property-reference". */
    readonly source: string;
    /**
     * How the request affects inspection state:
     * - `"focus"` (default) — the object becomes the inspection BASE:
     *   target-following panels (code, node graph) re-collect, override
     *   layers clear, {@link IInspectionService.getCurrentTarget} returns it.
     * - `"reveal"` — show the object in the Inspector/preview ONLY (the
     *   request event still fires); base and override layers stay untouched.
     *   For "open this thing while keeping the current context" — e.g.
     *   opening a render target's texture from the frame graph must not
     *   replace the very graph that was clicked.
     */
    readonly effect?: "focus" | "reveal";
}

/**
 * One target in the current inspection state: the object plus an optional
 * display label (e.g. the node-graph title of the material it belongs to) and
 * optional per-consumer payloads for content the target object alone can't
 * express — e.g. `payloads.code` carries explicit code options for a TSL Fn
 * body. Payloads are opaque here; consumers read the keys they understand.
 */
export interface InspectionTargetRef {
    readonly target: object;
    readonly label?: string;
    readonly payloads?: Record<string, unknown>;
}

/** Fired on {@link IInspectionService.onDidTargetsChange}. */
export interface InspectionTargetsChange {
    /**
     * True when the BASE layer changed (selection / inspect / clear) — which
     * also cleared any overrides. False for override-only changes; consumers
     * that only care about the base (e.g. the node-graph root) can skip those.
     */
    readonly baseChanged: boolean;
}

/**
 * Broadcast channel + state for "what is the user inspecting", decoupling
 * publishers (Resources panel, object-reference links, pickers, AI tools,
 * node-graph drill-downs) from consumers (Inspector, code panel, node graph,
 * future preview panels).
 *
 * Two layers decide the current targets:
 *  - **base** — the hierarchy selection, or the most recent {@link inspect}
 *    request (which replaces the selection-derived base until the selection
 *    next changes). Held weakly — inspecting must never extend a host-page
 *    object's lifetime.
 *  - **overrides** — named temporary layers on top of the base, set via
 *    {@link setOverride} (e.g. a node-graph drill-down level, a clicked
 *    function node). The LATEST-inserted layer wins; removing it reveals the
 *    layer below (that's what makes "deselect restores what you looked at
 *    before" work). Any base change drops all overrides.
 *
 * Per-consumer concerns stay per-consumer: lock (freeze what a panel shows)
 * and tab visibility are panel policy — a locked panel simply ignores
 * {@link onDidTargetsChange}.
 */
export interface IInspectionService {
    readonly _serviceBrand: undefined;

    /** Fires with the request on {@link inspect}, and with null on {@link clear}. */
    readonly onDidRequestInspection: Event<InspectRequest | null>;

    /**
     * The most recent request, for late-mounting consumers. Weakly held —
     * undefined once cleared or after the target was garbage collected.
     */
    getCurrentTarget(): InspectRequest | undefined;

    /**
     * The most recent `"reveal"` request (see {@link InspectRequest.effect}) —
     * a reveal doesn't become the base, but it IS the user's latest pointing
     * gesture (e.g. clicking a pass thumbnail opened this texture), so the AI
     * needs to see it ("check the texture I selected"). Weakly held; dropped
     * on any base change (a real navigation supersedes it).
     */
    getLastReveal(): InspectRequest | undefined;

    inspect(request: InspectRequest): void;
    clear(): void;

    // #region target layers

    /** Fires whenever the current targets change (base or override). */
    readonly onDidTargetsChange: Event<InspectionTargetsChange>;

    /**
     * The current targets: the top override layer, else the base. `"base"`
     * skips the overrides (e.g. the node-graph root always shows the base).
     * Weak base refs are deref'd on read; dead ones are dropped.
     */
    getTargets(layer?: "effective" | "base"): InspectionTargetRef[];

    /**
     * The target(s) the user is POINTING AT for resource-oriented AI read tools
     * (`nodegraph_read`, `property_find`, `property_read`): the latest reveal if
     * one is live, else {@link getTargets}("effective"). A reveal is the freshest
     * deliberate gesture (any base/selection navigation clears it), so "this
     * texture I just clicked in the graph" resolves to it — without disturbing
     * the inspection base that the code panel / `code_read` follow. Keeps the AI
     * consistent with the ambient context, which already reports the reveal.
     */
    getFocusTargets(): InspectionTargetRef[];

    /** True while any override layer is set. */
    readonly hasOverride: boolean;

    /**
     * Set (or replace) the override layer named `source`; `null`/empty removes
     * it. Layers stack in insertion order — the latest-inserted wins — so e.g.
     * a clicked function node ("nodegraph.node") sits on top of the drill-down
     * level ("nodegraph.level"), and removing it reveals the level again.
     *
     * Override refs are held STRONGLY (their payloads capture live objects
     * anyway); their lifetime is bounded — any base change clears all
     * overrides, and the pushing panel removes its layers on teardown.
     */
    setOverride(source: string, refs: readonly InspectionTargetRef[] | null): void;

    /** Remove all override layers (e.g. leaving the view that pushed them). */
    clearOverrides(): void;

    // #endregion
}
